import type { EmotionRuleConfig } from '../../types';

export interface EmotionThresholdMaps {
  minScoreByEmotion: Record<string, number>;
  strongInstantScoreByEmotion: Record<string, number>;
}

const DEFAULT_EMOTION_RULES: EmotionRuleConfig[] = [
  {
    name: 'happy',
    valence: 0.8,
    arousal: 0.6,
    rules: [
      { keys: ['mouthSmileLeft', 'mouthSmileRight'], weight: 0.45, threshold: 0.15 },
      { keys: ['cheekSquintLeft', 'cheekSquintRight'], weight: 0.30, threshold: 0.10 },
      { keys: ['eyeSquintLeft', 'eyeSquintRight'], weight: 0.25, threshold: 0.10 },
    ],
  },
  {
    name: 'sad',
    valence: -0.6,
    arousal: 0.2,
    rules: [
      { keys: ['mouthFrownLeft', 'mouthFrownRight'], weight: 0.40, threshold: 0.04 },
      { keys: ['mouthShrugLower'], weight: 0.45, threshold: 0.18 },
      { keys: ['mouthShrugUpper'], weight: 0.12, threshold: 0.10 },
      { keys: ['browInnerUp'], weight: 0.14, threshold: 0.09 },
      { keys: ['browDownLeft', 'browDownRight'], weight: 0.14, threshold: 0.18 },
      { keys: ['eyeLookDownLeft', 'eyeLookDownRight'], weight: 0.18, threshold: 0.08 },
      { keys: ['mouthRollLower'], weight: 0.10, threshold: 0.04 },
      { keys: ['jawForward'], weight: 0.08, threshold: 0.05 },
    ],
  },
  {
    name: 'angry',
    valence: -0.8,
    arousal: 0.7,
    rules: [
      { keys: ['browDownLeft', 'browDownRight'], weight: 0.20, threshold: 0.20 },
      { keys: ['eyeSquintLeft', 'eyeSquintRight'], weight: 0.34, threshold: 0.20 },
      { keys: ['mouthPressLeft', 'mouthPressRight'], weight: 0.24, threshold: 0.10 },
      { keys: ['noseSneerLeft', 'noseSneerRight'], weight: 0.15, threshold: 0.10 },
      { keys: ['jawForward'], weight: 0.15, threshold: 0.10 },
    ],
  },
  {
    name: 'fear',
    valence: -0.7,
    arousal: 0.8,
    rules: [
      { keys: ['eyeWideLeft', 'eyeWideRight'], weight: 0.30, threshold: 0.15 },
      { keys: ['browInnerUp'], weight: 0.25, threshold: 0.20 },
      { keys: ['browOuterUpLeft', 'browOuterUpRight'], weight: 0.15, threshold: 0.10 },
      { keys: ['mouthStretchLeft', 'mouthStretchRight'], weight: 0.20, threshold: 0.10 },
      { keys: ['jawOpen'], weight: 0.10, threshold: 0.10 },
    ],
  },
  {
    name: 'surprise',
    valence: 0.3,
    arousal: 0.9,
    rules: [
      { keys: ['eyeWideLeft', 'eyeWideRight'], weight: 0.30, threshold: 0.17 },
      { keys: ['browInnerUp'], weight: 0.30, threshold: 0.20 },
      { keys: ['browOuterUpLeft', 'browOuterUpRight'], weight: 0.20, threshold: 0.10 },
      { keys: ['jawOpen'], weight: 0.20, threshold: 0.15 },
    ],
  },
  {
    name: 'disgust',
    valence: -0.7,
    arousal: 0.5,
    rules: [
      { keys: ['noseSneerLeft', 'noseSneerRight'], weight: 0.40, threshold: 0.15 },
      { keys: ['mouthUpperUpLeft', 'mouthUpperUpRight'], weight: 0.30, threshold: 0.10 },
      { keys: ['mouthFrownLeft', 'mouthFrownRight'], weight: 0.20, threshold: 0.10 },
      { keys: ['mouthRollLower'], weight: 0.10, threshold: 0.10 },
    ],
  },
];

function cloneRuleConfig(config: EmotionRuleConfig): EmotionRuleConfig {
  return {
    ...config,
    rules: config.rules.map((rule) => ({ ...rule, keys: [...rule.keys] })),
  };
}

export function resolveEmotionRuleConfigs(customRules?: EmotionRuleConfig[]): EmotionRuleConfig[] {
  const byName = new Map<string, EmotionRuleConfig>();
  for (const rule of DEFAULT_EMOTION_RULES) {
    byName.set(rule.name, cloneRuleConfig(rule));
  }
  for (const rule of customRules ?? []) {
    byName.set(rule.name, cloneRuleConfig(rule));
  }
  return Array.from(byName.values());
}

export function buildEmotionThresholdMaps(customRules?: EmotionRuleConfig[]): EmotionThresholdMaps {
  const minScoreByEmotion: Record<string, number> = {};
  const strongInstantScoreByEmotion: Record<string, number> = {};

  for (const rule of customRules ?? []) {
    if (typeof rule.minScore === 'number') {
      minScoreByEmotion[rule.name] = rule.minScore;
    }
    if (typeof rule.strongInstantScore === 'number') {
      strongInstantScoreByEmotion[rule.name] = rule.strongInstantScore;
    }
  }

  return { minScoreByEmotion, strongInstantScoreByEmotion };
}